import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { usePolling } from "@/hooks/usePolling";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Buildings, Plus, Pencil, Trash } from "@phosphor-icons/react";

export default function AdminChaptersPage() {
  const { token } = useAuth();
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingChapter, setEditingChapter] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "", location: "" });

  const fetchChapters = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/chapters`, { headers: { Authorization: `Bearer ${token}` } });
      setChapters(response.data);
    } catch (error) { console.error("Failed to fetch chapters:", error); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchChapters(); }, [fetchChapters]);
  usePolling(fetchChapters, 15000);

  const handleSubmit = async () => {
    try {
      if (editingChapter) {
        await axios.put(`${API}/chapters/${editingChapter.id}`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Chapter updated");
      } else {
        await axios.post(`${API}/chapters`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Chapter created");
      }
      setDialogOpen(false); setEditingChapter(null); setFormData({ name: "", description: "", location: "" }); fetchChapters();
    } catch (error) { toast.error(error.response?.data?.detail || "Operation failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this chapter?")) return;
    try {
      await axios.delete(`${API}/chapters/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Chapter deleted"); fetchChapters();
    } catch (error) { toast.error("Failed to delete chapter"); }
  };

  return (
    <>
      <SEO title="Chapters Management" description="Manage BCH chapters — create, edit, and monitor chapter performance." path="/admin/chapters" noIndex />
      <div className="space-y-6 animate-fadeInUp" data-testid="admin-chapters-page">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl md:text-4xl text-primary">Chapters</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-bch-teal hover:bg-bch-teal-dark rounded-xl" onClick={() => { setEditingChapter(null); setFormData({ name: "", description: "", location: "" }); }}>
                <Plus className="w-5 h-5 mr-2" /> Add Chapter
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-[var(--border-color)]">
              <DialogHeader><DialogTitle className="text-primary">{editingChapter ? "Edit Chapter" : "Create Chapter"}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className="text-secondary">Chapter Name</Label>
                  <Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} className="bg-surface border-[var(--border-color)] text-primary" placeholder="Mumbai Central" />
                </div>
                <div className="space-y-2">
                  <Label className="text-secondary">Location</Label>
                  <Input value={formData.location} onChange={(e) => setFormData(p => ({ ...p, location: e.target.value }))} className="bg-surface border-[var(--border-color)] text-primary" placeholder="Mumbai, Maharashtra" />
                </div>
                <div className="space-y-2">
                  <Label className="text-secondary">Description</Label>
                  <Input value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} className="bg-surface border-[var(--border-color)] text-primary" placeholder="Premier business chapter" />
                </div>
              </div>
              <DialogFooter><Button onClick={handleSubmit} className="bg-bch-gold hover:bg-bch-gold-dark text-black rounded-xl">{editingChapter ? "Update" : "Create"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-bch-teal border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {chapters.map((chapter) => (
              <div key={chapter.id} className="bg-card border border-[var(--border-color)] rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-sky-300/40">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center shadow-md">
                    <Buildings className="w-5 h-5 text-white" weight="fill" />
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditingChapter(chapter); setFormData({ name: chapter.name, description: chapter.description, location: chapter.location }); setDialogOpen(true); }} className="p-2 hover:bg-surface rounded-xl transition-colors">
                      <Pencil className="w-4 h-4 text-secondary" />
                    </button>
                    <button onClick={() => handleDelete(chapter.id)} className="p-2 hover:bg-red-500/10 rounded-xl transition-colors">
                      <Trash className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>
                <h3 className="text-primary text-xl font-semibold mb-1">{chapter.name}</h3>
                <p className="text-muted text-sm mb-4">{chapter.location}</p>
                <div className="flex items-center justify-between pt-4 border-t border-[var(--border-color)]">
                  <span className="text-secondary text-sm font-medium">{chapter.member_count} members</span>
                  <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-xs font-semibold">Active</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
